import {
  LocationDeliveryRowSchema,
  type LocationDeliveriesQuery,
  type LocationDeliveriesResponse,
} from '../schemas/locationDeliveries';
import { isoDaysFromNow } from './deliveryDetail';

// Rows in the wire shape the read delivers, parsed through the schema so the mock can never
// drift from the contract. Dates are RELATIVE: the dialog opens on «Últimos 90 días», so a
// hardcoded date would render the happy path empty the moment it aged past that window.
// Grouped by carrier, newest first within each group. One timezone throughout: a building is
// one locker — and one address throughout, for the same reason.
//
// That address is the «Calle Bolivia 39» of the address-search fixture WITHOUT its `(BNB00)`
// suffix, which is the whole point: the suffix belongs to a PORTAL string the agent picked from a
// search, and a delivery row carries the building's own address. Two strings, two different data,
// and the fixture is where that stops being a claim. Its price is visible here too — the sibling
// building `(BNB01)` composes to this very string, so a paste tells the two apart by the box, not
// by the address.
//
// The carrier a row belongs to lives BESIDE the row, not inside it: the delivered row carries
// a display carrier name and no carrier id, while the filter is keyed by a carrier's integer
// id. Keeping the id out of the wire rows is what lets them mirror the delivered keys exactly,
// which is what the data-minimization assertion reads.
//
// Titulars are INVENTED — ids, names and phones, none copied from production — and spread along
// the reference carrier's range ladder so all three of their states are reachable in a dev run,
// not merely parseable: someone to call, a titular whose row could not be read (two «—» over a
// live id), and no titular at all. Phones are E.164 with no separators, which is the shape the
// column behind them holds and therefore the shape the panel renders verbatim.
const FIXTURE = [
  {
    carrierId: 4800,
    row: {
      id: 'f0000000-0000-4000-8000-000000000001',
      status: 'stored',
      stored_date: isoDaysFromNow(-2),
      collected_date: null,
      box_number: 'DZW05',
      tracking: '1Z5C-AMZ-51002',
      carrier: 'Amazon',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      // The long name, on the NEWEST row of the reference carrier: the truncation is reviewable
      // on first run rather than first incident, whichever range the dialog opens on.
      recipient: { id: 8801, name: 'María del Carmen Fernández de la Fuente', phone: '+34600000001' },
    },
  },
  {
    carrierId: 4800,
    row: {
      id: 'f0000000-0000-4000-8000-000000000002',
      status: 'collected',
      stored_date: isoDaysFromNow(-10),
      collected_date: isoDaysFromNow(-9),
      box_number: 'DZW03',
      tracking: '1Z5C-AMZ-64019',
      carrier: 'Amazon',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      recipient: { id: 8802, name: 'Ana Ruiz', phone: '+34600000002' },
    },
  },
  {
    carrierId: 4800,
    row: {
      id: 'f0000000-0000-4000-8000-000000000003',
      status: 'expired',
      stored_date: isoDaysFromNow(-20),
      collected_date: null,
      box_number: 'DZW11',
      tracking: '1Z5C-AMZ-31877',
      carrier: 'Amazon',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      // A titular that EXISTS and could not be read — dangling id or a deactivated «Usuario».
      // Both cells paint «—», same as no titular at all, and the id is the only thing that
      // tells the two apart. Without this row the distinction lives only in the schema.
      recipient: { id: 8803, name: null, phone: null },
    },
  },
  {
    carrierId: 4800,
    row: {
      id: 'f0000000-0000-4000-8000-000000000004',
      status: 'retrieved',
      stored_date: isoDaysFromNow(-45),
      collected_date: isoDaysFromNow(-43),
      box_number: 'DZW07',
      tracking: '1Z5C-AMZ-20554',
      carrier: 'Amazon',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      recipient: { id: 8804, name: 'Jorge Peña', phone: '+34600000004' },
    },
  },
  {
    carrierId: 4800,
    row: {
      id: 'f0000000-0000-4000-8000-000000000005',
      status: 'non_collected',
      stored_date: isoDaysFromNow(-80),
      collected_date: null,
      box_number: 'DZW12',
      tracking: '1Z5C-AMZ-99013',
      carrier: 'Amazon',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      // On file with a name and no phone: absence is per field, not per titular.
      recipient: { id: 8805, name: 'Lucía Ibáñez', phone: null },
    },
  },
  // A delivery that never reached a box: no stored date, no box. It belongs to the OTHER
  // carrier called «Amazon», so the reference carrier's one-row-per-band ladder stays readable
  // while the shape a tenth of a busy building's rows arrive in is still reachable in a dev run.
  {
    carrierId: 5127,
    row: {
      id: 'f0000000-0000-4000-8000-000000000006',
      status: 'cancelled',
      stored_date: null,
      collected_date: null,
      box_number: null,
      tracking: '1Z5C-AMZ-70414',
      carrier: 'Amazon',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      // No titular at all, on the delivery that never reached a box — the state backend
      // documented for a `cancelled` that was never assigned.
      recipient: null,
    },
  },
  {
    carrierId: 1201,
    row: {
      id: 'f0000000-0000-4000-8000-000000000007',
      status: 'stored',
      stored_date: isoDaysFromNow(-1),
      collected_date: null,
      box_number: 'DZW02',
      tracking: 'CO-4471209ES',
      carrier: 'Correos',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      recipient: { id: 8807, name: 'Rosa Calvo', phone: '+34600000007' },
    },
  },
  {
    carrierId: 1440,
    row: {
      id: 'f0000000-0000-4000-8000-000000000008',
      status: 'collected',
      stored_date: isoDaysFromNow(-3),
      collected_date: isoDaysFromNow(-2),
      box_number: 'DZW08',
      tracking: 'GLS-88120043',
      carrier: 'GLS',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      recipient: { id: 8808, name: 'Iván Sedano', phone: '+34600000008' },
    },
  },
  {
    carrierId: 1622,
    row: {
      id: 'f0000000-0000-4000-8000-000000000009',
      status: 'stored',
      stored_date: isoDaysFromNow(-5),
      collected_date: null,
      box_number: 'DZW04',
      tracking: 'SEUR-55901772',
      carrier: 'SEUR',
      locker_timezone: 'Europe/Madrid',
      location: { address: 'Calle Bolivia 39, 28020 Madrid' },
      recipient: { id: 8809, name: 'Nuria Alcaraz Bermejo', phone: '+34600000009' },
    },
  },
];

// Exported raw because the wire shape is what the data-minimization assertion reads: a parsed
// row can no longer show a key the schema stripped.
export const locationDeliveriesFixture = FIXTURE.map(({ row }) => row);

// Which catalog carriers have rows behind them, so the catalog mock can prove it offers no
// carrier that is a dead end without reaching into this module's row data.
export const locationDeliveriesFixtureCarrierIds: readonly number[] = [
  ...new Set(FIXTURE.map(({ carrierId }) => carrierId)),
];

const DAY_MS = 86_400_000;

// A row with no stored date cannot be placed in a window by a date it does not have, and the
// read does return those rows, so the mock keeps them in every range rather than hiding them.
function withinWindow(storedDate: string | null, days: number): boolean {
  return storedDate === null || Date.parse(storedDate) >= Date.now() - days * DAY_MS;
}

// «Buzón» is an EXACT match, never a prefix: the full matrícula in any case («DZW05», «dzw05»)
// or its number with or without the leading zero («5», «05»). «50» and a bare «DZW» match
// nothing, and a blank or whitespace-only value is no filter at all rather than a filter no row
// can satisfy. A row that never reached a box matches no «Buzón».
function matchesBox(boxNumber: string | null, box: string | undefined): boolean {
  const needle = box?.trim() ?? '';
  if (needle === '') return true;
  if (boxNumber === null) return false;
  if (needle.toLowerCase() === boxNumber.toLowerCase()) return true;
  const number = /(\d+)$/.exec(boxNumber)?.[1];
  return /^\d+$/.test(needle) && number !== undefined && Number(needle) === Number(number);
}

// An absent `carrier` is the wire form of «every carrier», so it filters nothing.
function matchesCarrier(carrierId: number, carrier: number | undefined): boolean {
  return carrier === undefined || carrierId === carrier;
}

// Same grammar of absence: no state filter is «every state». The row's own field is `status` —
// the one place the wire keeps that spelling — while the filter that selects over it is a set of
// states.
function matchesState(status: string, states: string[] | undefined): boolean {
  return states === undefined || states.includes(status);
}

// The location id is accepted and ignored: one building's fixture serves every match until the
// read lands.
export async function fetchLocationDeliveriesMock(
  _locationId: number,
  query: LocationDeliveriesQuery,
): Promise<LocationDeliveriesResponse> {
  // Simulate network latency for a realistic dev experience.
  await new Promise((resolve) => setTimeout(resolve, 100));
  const deliveries = FIXTURE.filter(
    ({ carrierId, row }) =>
      matchesCarrier(carrierId, query.carrier) &&
      withinWindow(row.stored_date, query.days) &&
      matchesBox(row.box_number, query.box) &&
      matchesState(row.status, query.states),
  ).map(({ row }) => LocationDeliveryRowSchema.parse(row));
  // The read never caps or paginates, so the count is always what came back.
  return { count: deliveries.length, deliveries };
}
